import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/currencyUtils";
import { normalizeDecimalInput, parseDecimalSafe } from "@/lib/numberUtils";
import { History, TrendingUp, TrendingDown, Minus, CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { HelpTip } from "@/components/ui/help-tip";

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Append-only price ledger modal.
 * - Shows the full historical ledger for an ingredient (newest first).
 * - Lets the user append a new row (bulk_price + bulk_quantity + effective_date + notes).
 * - Never overwrites the past — backend enforces this.
 */
export default function LogPriceModal({
  isOpen,
  onClose,
  ingredient,
  currency,
  onLogged,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    bulk_price: "",
    bulk_quantity: "",
    effective_date: todayIso(),
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset state + fetch history on open
  useEffect(() => {
    if (!isOpen || !ingredient) return;
    setForm({
      bulk_price: "",
      bulk_quantity: ingredient.bulk_quantity || "",
      effective_date: todayIso(),
      notes: "",
    });
    setErrors({});
    let cancelled = false;
    setLoadingHistory(true);
    axios
      .get(`${API}/ingredients/${ingredient.id}/price-history`)
      .then((res) => {
        if (cancelled) return;
        setHistory(res.data?.history || []);
      })
      .catch(() => {
        if (cancelled) return;
        setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, ingredient]);

  const previewUnitCost = useMemo(() => {
    const p = parseDecimalSafe(form.bulk_price);
    const q = parseDecimalSafe(form.bulk_quantity);
    if (!p || !q || p <= 0 || q <= 0) return null;
    return (p / q).toFixed(6);
  }, [form.bulk_price, form.bulk_quantity]);

  const currentUnitCost = ingredient?.unit_cost
    ? parseFloat(ingredient.unit_cost)
    : null;
  const deltaPct = useMemo(() => {
    if (!previewUnitCost || !currentUnitCost) return null;
    const diff = (parseFloat(previewUnitCost) - currentUnitCost) / currentUnitCost;
    return diff * 100;
  }, [previewUnitCost, currentUnitCost]);

  const handleChange = (field, value) => {
    const v =
      field === "bulk_price" || field === "bulk_quantity"
        ? normalizeDecimalInput(value)
        : value;
    setForm((prev) => ({ ...prev, [field]: v }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.bulk_price || parseFloat(form.bulk_price) <= 0)
      errs.bulk_price = t("common.required");
    if (!form.bulk_quantity || parseFloat(form.bulk_quantity) <= 0)
      errs.bulk_quantity = t("common.required");
    if (!form.effective_date) errs.effective_date = t("common.required");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!ingredient?.id) return;
    if (!validate()) {
      toast.error(t("logPrice.errorSubmit"));
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(
        `${API}/ingredients/${ingredient.id}/price-history`,
        {
          bulk_price: form.bulk_price,
          bulk_quantity: form.bulk_quantity,
          unit: ingredient.unit,
          effective_date: form.effective_date,
          notes: form.notes || null,
        }
      );
      if (res.data?.applies_now) {
        toast.success(t("logPrice.appliedNow"));
      } else {
        toast.success(t("logPrice.scheduledFuture"));
      }
      onLogged?.(res.data);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || t("logPrice.errorSubmit");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const deltaIcon = () => {
    if (deltaPct === null) return null;
    if (Math.abs(deltaPct) < 0.01)
      return <Minus className="h-4 w-4 text-slate-400" />;
    return deltaPct > 0 ? (
      <TrendingUp className="h-4 w-4 text-red-400" />
    ) : (
      <TrendingDown className="h-4 w-4 text-emerald-400" />
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="log-price-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-amber-500 flex items-center gap-2">
            <History className="h-5 w-5" />
            {t("logPrice.title")}{ingredient?.name ? ` — ${ingredient.name}` : ""}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {t("logPrice.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Current baseline */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">
                {t("logPrice.currentPrice")}
              </div>
              <div className="text-lg font-mono text-white mt-1">
                {ingredient
                  ? `${formatCurrency(ingredient.bulk_price, currency)} / ${ingredient.bulk_quantity} ${ingredient.unit}`
                  : "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-400">
                {t("logPrice.unitCost")}
              </div>
              <div className="text-lg font-mono text-amber-400 mt-1">
                {ingredient
                  ? `${formatCurrency(ingredient.unit_cost, currency, 6)} / ${ingredient.unit}`
                  : "—"}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center">
                {t("logPrice.newBulkPrice")} *
                <HelpTip title={t("logPrice.newBulkPrice")} testId="help-bulk-price">
                  <span dangerouslySetInnerHTML={{ __html: t("logPrice.bulkPriceHelp") }} />
                </HelpTip>
              </Label>
              <Input
                type="text"
                inputMode="decimal"
                value={form.bulk_price}
                onChange={(e) => handleChange("bulk_price", e.target.value)}
                placeholder={t("logPrice.newBulkPricePlaceholder")}
                className="bg-slate-800 border-slate-700 text-white font-mono"
                data-testid="log-price-bulk-price"
              />
              {errors.bulk_price && (
                <p className="text-xs text-red-400">{errors.bulk_price}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center">
                {t("logPrice.bulkQuantity")} ({ingredient?.unit || ""}) *
                <HelpTip title={t("logPrice.bulkQuantity")} testId="help-bulk-quantity">
                  <span dangerouslySetInnerHTML={{ __html: t("logPrice.bulkQuantityHelp") }} />
                </HelpTip>
              </Label>
              <Input
                type="text"
                inputMode="decimal"
                value={form.bulk_quantity}
                onChange={(e) => handleChange("bulk_quantity", e.target.value)}
                placeholder={t("logPrice.bulkQuantityPlaceholder")}
                className="bg-slate-800 border-slate-700 text-white font-mono"
                data-testid="log-price-bulk-quantity"
              />
              {errors.bulk_quantity && (
                <p className="text-xs text-red-400">{errors.bulk_quantity}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center">
                {t("logPrice.effectiveDate")} *
                <HelpTip title={t("logPrice.effectiveDate")} testId="help-effective-date">
                  <span dangerouslySetInnerHTML={{ __html: t("logPrice.effectiveDateHelp") }} />
                </HelpTip>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="log-price-effective-date"
                    className={cn(
                      "w-full justify-start text-left font-normal bg-slate-800 border-slate-700 text-white hover:bg-slate-800/80 hover:text-white",
                      !form.effective_date && "text-slate-400"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-amber-500" />
                    {form.effective_date
                      ? format(parseISO(form.effective_date), "PPP")
                      : t("logPrice.pickDate")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0 bg-slate-900 border-slate-700"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={form.effective_date ? parseISO(form.effective_date) : undefined}
                    onSelect={(date) =>
                      handleChange(
                        "effective_date",
                        date ? format(date, "yyyy-MM-dd") : ""
                      )
                    }
                    initialFocus
                    className="bg-slate-900 text-white"
                  />
                </PopoverContent>
              </Popover>
              {errors.effective_date && (
                <p className="text-xs text-red-400">{errors.effective_date}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">{t("logPrice.newUnitCost")}</Label>
              <div
                className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-amber-400 font-mono flex items-center justify-between"
                data-testid="log-price-preview-unit-cost"
              >
                <span>
                  {previewUnitCost
                    ? `${formatCurrency(previewUnitCost, currency, 6)} / ${ingredient?.unit || ""}`
                    : "—"}
                </span>
                <span className="flex items-center gap-1 text-xs">
                  {deltaIcon()}
                  {deltaPct !== null && Math.abs(deltaPct) >= 0.01 && (
                    <span
                      className={
                        deltaPct > 0 ? "text-red-400" : "text-emerald-400"
                      }
                    >
                      {deltaPct > 0 ? "+" : ""}
                      {deltaPct.toFixed(1)}%
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">
              {t("logPrice.notesOptional")} <span className="text-slate-500">({t("common.optional")})</span>
            </Label>
            <Textarea
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder={t("logPrice.notesPlaceholder")}
              rows={2}
              className="bg-slate-800 border-slate-700 text-white resize-none"
              data-testid="log-price-notes"
            />
          </div>

          {/* Ledger */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <History className="h-4 w-4 text-amber-500" />
                {t("logPrice.priceLedger")}
              </Label>
              <span className="text-xs text-slate-500">
                {history.length} {history.length === 1 ? t("logPrice.entry") : t("logPrice.entries")}
              </span>
            </div>
            <div
              className="border border-slate-800 rounded-lg overflow-hidden"
              data-testid="log-price-history-list"
            >
              {loadingHistory ? (
                <div className="p-4 text-sm text-slate-400">{t("common.loading")}</div>
              ) : history.length === 0 ? (
                <div className="p-4 text-sm text-slate-500 italic">
                  {t("logPrice.noHistory")}
                </div>
              ) : (
                <div className="divide-y divide-slate-800 max-h-56 overflow-y-auto">
                  {history.map((h) => (
                    <div
                      key={h.id}
                      className="grid grid-cols-[100px_1fr_auto] items-center gap-3 px-4 py-2.5 text-sm"
                      data-testid={`log-price-history-row-${h.id}`}
                    >
                      <span className="text-slate-400 font-mono">
                        {h.effective_date}
                      </span>
                      <span className="text-slate-200">
                        {formatCurrency(h.bulk_price, currency)} / {h.bulk_quantity}{" "}
                        {h.unit}
                        {h.notes ? (
                          <span className="text-slate-500 italic ml-2">
                            · {h.notes}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-amber-400 font-mono text-xs">
                        {formatCurrency(h.unit_cost, currency, 6)}/{h.unit}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
            data-testid="log-price-cancel"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
            data-testid="log-price-submit"
          >
            {submitting ? t("logPrice.submittingButton") : t("logPrice.submitButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
